import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { dismissNotice } from '@/store/slices/lobbySlice';

const AUTO_DISMISS_MS = 4000;

/**
 * App-level toast for transient lobby events ("X joined", "X left"). Mounts
 * in App.tsx so the announcement is visible from every route, not just the
 * Watch page's chat panel. Auto-dismisses after AUTO_DISMISS_MS.
 *
 * Keyed off `notice.nonce` so a repeat event (same user joins again) still
 * resets the auto-dismiss timer.
 */
export function LobbyNotice(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const notice = useAppSelector((s) => s.lobby.notice);

  useEffect(() => {
    if (!notice) return undefined;
    const id = window.setTimeout(() => dispatch(dismissNotice()), AUTO_DISMISS_MS);
    return (): void => { window.clearTimeout(id); };
  }, [notice?.nonce, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!notice) return null;

  return (
    <div
      className={`lobby-notice lobby-notice--${notice.kind}`}
      role="status"
      aria-live="polite"
      data-testid="lobby-notice"
      onClick={(): void => { dispatch(dismissNotice()); }}
    >
      <span className="lobby-notice__dot" aria-hidden="true" />
      <span className="lobby-notice__text">
        <Trans
          i18nKey={notice.kind === 'joined' ? 'lobby.notice.joined' : 'lobby.notice.left'}
          values={{ name: notice.displayName }}
          components={{ strong: <strong /> }}
        />
      </span>
    </div>
  );
}
